import { Link } from 'react-router-dom';
import { Layout } from '../components/layout/Layout';
import { Button } from '../components/ui/button';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { HelpCircle, MessageCircle } from 'lucide-react';

const faqData = [
  {
    id: '1',
    question: 'What is NextGig?',
    answer: 'NextGig is an event ticketing and promotion platform that helps you discover events near you and allows organizers to create, promote, and sell tickets.'
  },
  {
    id: '2',
    question: 'How do I find events near me?',
    answer: 'Simply browse the platform and allow location access (if enabled). NextGig automatically shows events happening around you—so you never miss out.'
  },
  {
    id: '3',
    question: 'How do I buy a ticket?',
    answer: 'Select an event, choose your ticket type, and complete the checkout process. Once payment is successful, your ticket will be sent to your email or available in your account.'
  },
  {
    id: '4',
    question: "Can I get a refund if I can't attend an event?",
    answer: "Yes. Refunds are available based on the event's refund policy. NextGig is built with a fair and transparent refund system, but we recommend checking each event's terms before purchasing."
  },
  {
    id: '5',
    question: 'How do I host an event on NextGig?',
    answer: 'Sign up as an organizer, create your event, set ticket prices, and publish. You can start promoting and selling tickets immediately.'
  },
  {
    id: '6',
    question: 'How do organizers get paid?',
    answer: 'We offer fast and reliable payouts. Once tickets are sold, organizers receive their earnings according to our payout schedule—without unnecessary delays.'
  },
  {
    id: '7',
    question: 'Is my payment information secure?',
    answer: 'Yes. We use secure payment processing systems to ensure your data and transactions are protected.'
  },
  {
    id: '8',
    question: 'What types of events can I find on NextGig?',
    answer: 'You can find a wide range of events including concerts, parties, tech meetups, workshops, networking events, and more.'
  },
  {
    id: '9',
    question: 'Do I need an account to use NextGig?',
    answer: "You can browse events without an account, but you'll need to sign up to purchase free tickets or host events. However, you can buy ticket and checkout as guest without signing up."
  },
  {
    id: '10',
    question: 'What countries does NextGig operate in?',
    answer: 'NextGig primarily serves users in Nigeria and Canada, with plans to expand to more regions.'
  },
  {
    id: '11',
    question: 'What happens if an event is cancelled?',
    answer: "If an event is cancelled, you will be notified and eligible for a refund based on the organizer's policy."
  },
  {
    id: '12',
    question: 'How can I contact NextGig?',
    answer: 'You can reach out through our Contact Us page or email us at Nextgigsuite@gmail.com.'
  }
];

export default function FAQ() {
  return (
    <Layout>
      <div className="min-h-screen py-12 md:py-20">
        {/* Hero Section */}
        <div className="max-w-3xl mx-auto px-4 md:px-8 text-center mb-12">
          <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto mb-6">
            <HelpCircle className="w-8 h-8 text-primary" />
          </div>
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-4">
            Frequently Asked Questions
          </h1>
          <p className="text-lg text-muted-foreground max-w-xl mx-auto">
            Got questions? We've got answers. Find everything you need to know about using NextGig.
          </p>
        </div>

        {/* FAQ Accordion */}
        <div className="max-w-3xl mx-auto px-4 md:px-8">
          <Accordion type="single" collapsible className="w-full space-y-4">
            {faqData.map((faq, index) => (
              <AccordionItem 
                key={faq.id} 
                value={faq.id}
                className="border border-border rounded-xl px-6 bg-background hover:border-primary/50 transition-colors data-[state=open]:border-primary/50 data-[state=open]:bg-primary/5"
              >
                <AccordionTrigger className="text-left text-base md:text-lg font-medium py-5 hover:no-underline text-foreground">
                  <span className="flex items-start gap-3">
                    <span className="text-primary font-bold min-w-[28px]">{index + 1}.</span>
                    <span>{faq.question}</span>
                  </span>
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground text-base leading-relaxed pl-10 pb-5">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>

        {/* CTA Section */}
        <div className="max-w-3xl mx-auto px-4 md:px-8 mt-16">
          <div className="text-center p-8 md:p-12 rounded-2xl bg-gradient-to-br from-primary/10 via-background to-secondary/10 border border-border">
            <h2 className="text-2xl md:text-3xl font-bold mb-3">
              Still have questions?
            </h2>
            <p className="text-muted-foreground mb-8">
              Reach out to us—we're here to help.
            </p>
            <Link to="/contact">
              <Button size="lg" className="gap-2 h-12 px-8" data-testid="faq-contact-btn">
                <MessageCircle className="w-5 h-5" />
                Contact Us
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </Layout>
  );
}
